import { Link } from 'react-router-dom';
import { useAuth } from '../store/authStore';

/* ── Demo thumbnail cards (SVG) ─────────────────────────────────── */
const DEMO_CARDS = [
  /* 1 — Bar chart infographic */
  <svg key="bar" viewBox="0 0 160 160" className="w-full h-full bg-[#0f172a]">
    <rect width="160" height="160" fill="#0f172a"/>
    <text x="12" y="18" fontSize="7" fill="#94a3b8" fontFamily="sans-serif" fontWeight="bold">PRODUCTIVITY STATS</text>
    <text x="12" y="28" fontSize="5" fill="#475569" fontFamily="sans-serif">Annual Report 2024</text>
    {[
      { x: 18, h: 55, c: '#6366f1' },
      { x: 38, h: 80, c: '#818cf8' },
      { x: 58, h: 45, c: '#6366f1' },
      { x: 78, h: 95, c: '#a5b4fc' },
      { x: 98, h: 65, c: '#6366f1' },
      { x: 118, h: 110, c: '#818cf8' },
      { x: 138, h: 75, c: '#6366f1' },
    ].map(({ x, h, c }, i) => (
      <rect key={i} x={x} y={145 - h} width="14" height={h} rx="2" fill={c} opacity="0.85" />
    ))}
    <line x1="12" y1="145" x2="155" y2="145" stroke="#334155" strokeWidth="0.8"/>
    {['Q1','Q2','Q3','Q4','Q5','Q6','Q7'].map((l, i) => (
      <text key={l} x={25 + i * 20} y="154" fontSize="5" fill="#475569" fontFamily="sans-serif" textAnchor="middle">{l}</text>
    ))}
  </svg>,

  /* 2 — Book cover (dark novel) */
  <svg key="book1" viewBox="0 0 160 160" className="w-full h-full">
    <defs>
      <linearGradient id="bg1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#1e1b4b"/>
        <stop offset="100%" stopColor="#0f172a"/>
      </linearGradient>
    </defs>
    <rect width="160" height="160" fill="url(#bg1)"/>
    <circle cx="80" cy="58" r="32" fill="none" stroke="#6366f1" strokeWidth="1.5" opacity="0.6"/>
    <circle cx="80" cy="58" r="20" fill="#6366f1" opacity="0.15"/>
    <polygon points="80,36 88,52 104,54 92,66 95,82 80,74 65,82 68,66 56,54 72,52" fill="#818cf8" opacity="0.9"/>
    <rect x="20" y="96" width="120" height="1" fill="#6366f1" opacity="0.4"/>
    <text x="80" y="112" fontSize="11" fontWeight="bold" fill="white" fontFamily="sans-serif" textAnchor="middle">ATOMIC HABITS</text>
    <text x="80" y="124" fontSize="6" fill="#94a3b8" fontFamily="sans-serif" textAnchor="middle">JAMES CLEAR</text>
    <text x="80" y="138" fontSize="5" fill="#6366f1" fontFamily="sans-serif" textAnchor="middle">BESTSELLER · 2018</text>
  </svg>,

  /* 3 — World map / data viz */
  <svg key="map" viewBox="0 0 160 160" className="w-full h-full bg-[#0c1220]">
    <rect width="160" height="160" fill="#0c1220"/>
    <text x="80" y="14" fontSize="7" fontWeight="bold" fill="#e2e8f0" fontFamily="sans-serif" textAnchor="middle">GLOBAL REACH</text>
    {/* simplified continent blobs */}
    <ellipse cx="52" cy="78" rx="22" ry="18" fill="#1e3a5f" opacity="0.9"/>
    <ellipse cx="48" cy="92" rx="12" ry="16" fill="#1e3a5f" opacity="0.9"/>
    <ellipse cx="82" cy="72" rx="28" ry="22" fill="#1e3a5f" opacity="0.9"/>
    <ellipse cx="78" cy="92" rx="10" ry="18" fill="#1e3a5f" opacity="0.9"/>
    <ellipse cx="122" cy="70" rx="20" ry="16" fill="#1e3a5f" opacity="0.9"/>
    <ellipse cx="118" cy="90" rx="14" ry="20" fill="#1e3a5f" opacity="0.9"/>
    <ellipse cx="136" cy="78" rx="12" ry="12" fill="#1e3a5f" opacity="0.9"/>
    {/* hotspot dots */}
    {[
      [52,75,'#6366f1',5],[82,68,'#a5b4fc',7],[122,66,'#818cf8',4],
      [136,75,'#6366f1',3],[48,88,'#6366f1',3],[78,86,'#818cf8',3],
    ].map(([cx,cy,fill,r],i) => (
      <circle key={i} cx={cx} cy={cy} r={r} fill={fill} opacity="0.85"/>
    ))}
    <text x="80" y="148" fontSize="6" fill="#475569" fontFamily="sans-serif" textAnchor="middle">12 regions · 4.2M users tracked</text>
  </svg>,

  /* 4 — Pie / donut chart */
  <svg key="pie" viewBox="0 0 160 160" className="w-full h-full bg-[#0f172a]">
    <rect width="160" height="160" fill="#0f172a"/>
    <text x="80" y="14" fontSize="7" fontWeight="bold" fill="#e2e8f0" fontFamily="sans-serif" textAnchor="middle">MARKET SHARE</text>
    {/* donut slices via stroked circles */}
    <circle cx="80" cy="78" r="35" fill="none" stroke="#6366f1" strokeWidth="18"
      strokeDasharray="88 132" strokeDashoffset="0" opacity="0.9"/>
    <circle cx="80" cy="78" r="35" fill="none" stroke="#818cf8" strokeWidth="18"
      strokeDasharray="55 165" strokeDashoffset="-88" opacity="0.9"/>
    <circle cx="80" cy="78" r="35" fill="none" stroke="#a5b4fc" strokeWidth="18"
      strokeDasharray="33 187" strokeDashoffset="-143" opacity="0.9"/>
    <circle cx="80" cy="78" r="35" fill="none" stroke="#c7d2fe" strokeWidth="18"
      strokeDasharray="24 196" strokeDashoffset="-176" opacity="0.9"/>
    <circle cx="80" cy="78" r="22" fill="#0f172a"/>
    <text x="80" y="82" fontSize="9" fontWeight="bold" fill="white" fontFamily="sans-serif" textAnchor="middle">40%</text>
    <circle cx="22" cy="138" r="4" fill="#6366f1"/>
    <text x="30" y="141" fontSize="5" fill="#94a3b8" fontFamily="sans-serif">Product A</text>
    <circle cx="68" cy="138" r="4" fill="#818cf8"/>
    <text x="76" y="141" fontSize="5" fill="#94a3b8" fontFamily="sans-serif">Product B</text>
    <circle cx="112" cy="138" r="4" fill="#a5b4fc"/>
    <text x="120" y="141" fontSize="5" fill="#94a3b8" fontFamily="sans-serif">Other</text>
  </svg>,

  /* 5 — Book cover (bright) */
  <svg key="book2" viewBox="0 0 160 160" className="w-full h-full">
    <defs>
      <linearGradient id="bg2" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#f97316"/>
        <stop offset="100%" stopColor="#b45309"/>
      </linearGradient>
    </defs>
    <rect width="160" height="160" fill="url(#bg2)"/>
    <rect x="0" y="0" width="160" height="160" fill="black" opacity="0.35"/>
    {[0,1,2,3].map(i => (
      <line key={i} x1="0" y1={30+i*28} x2="160" y2={30+i*28} stroke="white" strokeWidth="0.4" opacity="0.15"/>
    ))}
    <text x="80" y="46" fontSize="8" fontWeight="bold" fill="white" fontFamily="sans-serif" textAnchor="middle" letterSpacing="3">DEEP WORK</text>
    <text x="80" y="60" fontSize="5.5" fill="rgba(255,255,255,0.7)" fontFamily="sans-serif" textAnchor="middle">Rules for Focused Success</text>
    <text x="80" y="72" fontSize="5.5" fill="rgba(255,255,255,0.7)" fontFamily="sans-serif" textAnchor="middle">in a Distracted World</text>
    <rect x="30" y="82" width="100" height="0.8" fill="white" opacity="0.5"/>
    <rect x="55" y="92" width="50" height="38" rx="4" fill="white" opacity="0.07"/>
    <text x="80" y="118" fontSize="5" fill="rgba(255,255,255,0.6)" fontFamily="sans-serif" textAnchor="middle">CAL NEWPORT</text>
    <rect x="55" y="136" width="50" height="12" rx="2" fill="white" opacity="0.15"/>
    <text x="80" y="145" fontSize="5" fill="white" fontFamily="sans-serif" textAnchor="middle">★★★★★  BESTSELLER</text>
  </svg>,

  /* 6 — Timeline infographic */
  <svg key="timeline" viewBox="0 0 160 160" className="w-full h-full bg-[#0a0f1e]">
    <rect width="160" height="160" fill="#0a0f1e"/>
    <text x="80" y="14" fontSize="7" fontWeight="bold" fill="#e2e8f0" fontFamily="sans-serif" textAnchor="middle">MILESTONES</text>
    <line x1="80" y1="24" x2="80" y2="148" stroke="#1e293b" strokeWidth="2"/>
    {[
      { y: 35, label: '2020', desc: 'Launch', c: '#6366f1', side: -1 },
      { y: 62, label: '2021', desc: 'Series A', c: '#818cf8', side: 1 },
      { y: 89, label: '2022', desc: '1M Users', c: '#a5b4fc', side: -1 },
      { y: 116, label: '2023', desc: 'Profitable', c: '#6366f1', side: 1 },
      { y: 143, label: '2024', desc: 'Global', c: '#818cf8', side: -1 },
    ].map(({ y, label, desc, c, side }) => (
      <g key={y}>
        <circle cx="80" cy={y} r="5" fill={c}/>
        <text x={80 + side * 12} y={y - 4} fontSize="6" fontWeight="bold" fill={c}
          fontFamily="sans-serif" textAnchor={side === -1 ? 'end' : 'start'}>{label}</text>
        <text x={80 + side * 12} y={y + 5} fontSize="5" fill="#94a3b8"
          fontFamily="sans-serif" textAnchor={side === -1 ? 'end' : 'start'}>{desc}</text>
      </g>
    ))}
  </svg>,

  /* 7 — Comparison / feature table */
  <svg key="compare" viewBox="0 0 160 160" className="w-full h-full bg-[#0f172a]">
    <rect width="160" height="160" fill="#0f172a"/>
    <text x="80" y="14" fontSize="7" fontWeight="bold" fill="#e2e8f0" fontFamily="sans-serif" textAnchor="middle">COMPARISON</text>
    <rect x="10" y="20" width="140" height="16" rx="3" fill="#1e293b"/>
    <text x="55" y="31" fontSize="6" fill="#64748b" fontFamily="sans-serif" textAnchor="middle">FEATURE</text>
    <text x="105" y="31" fontSize="6" fontWeight="bold" fill="#818cf8" fontFamily="sans-serif" textAnchor="middle">SNAPVAULT</text>
    <text x="145" y="31" fontSize="6" fill="#475569" fontFamily="sans-serif" textAnchor="middle">OTHER</text>
    {[
      ['One-click save', true, false],
      ['Folders & tags', true, false],
      ['Full search', true, false],
      ['Share links', true, false],
      ['Mobile PWA', true, false],
      ['Private vault', true, false],
    ].map(([feat, ours, theirs], i) => (
      <g key={feat}>
        <rect x="10" y={40 + i * 19} width="140" height="18" rx="2" fill={i % 2 === 0 ? '#0f172a' : '#111827'}/>
        <text x="14" y={52 + i * 19} fontSize="5.5" fill="#94a3b8" fontFamily="sans-serif">{feat}</text>
        <text x="105" y={52 + i * 19} fontSize="8" fill="#6366f1" fontFamily="sans-serif" textAnchor="middle">{ours ? '✓' : '✗'}</text>
        <text x="145" y={52 + i * 19} fontSize="8" fill="#475569" fontFamily="sans-serif" textAnchor="middle">{theirs ? '✓' : '✗'}</text>
      </g>
    ))}
  </svg>,

  /* 8 — Book cover (minimal teal) */
  <svg key="book3" viewBox="0 0 160 160" className="w-full h-full">
    <defs>
      <linearGradient id="bg3" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#0d9488"/>
        <stop offset="100%" stopColor="#0f766e"/>
      </linearGradient>
    </defs>
    <rect width="160" height="160" fill="url(#bg3)"/>
    <rect x="0" y="0" width="160" height="160" fill="black" opacity="0.2"/>
    {[0,1,2,3,4,5].map(i => (
      <rect key={i} x={10 + i*24} y="0" width="12" height="160" fill="white" opacity="0.03"/>
    ))}
    <circle cx="80" cy="62" r="30" fill="white" opacity="0.08"/>
    <circle cx="80" cy="62" r="18" fill="white" opacity="0.12"/>
    <text x="80" y="56" fontSize="18" fill="white" fontFamily="sans-serif" textAnchor="middle" opacity="0.9">📈</text>
    <text x="80" y="74" fontSize="6" fill="rgba(255,255,255,0.6)" fontFamily="sans-serif" textAnchor="middle">GROWTH FRAMEWORK</text>
    <rect x="20" y="88" width="120" height="0.8" fill="white" opacity="0.3"/>
    <text x="80" y="104" fontSize="10" fontWeight="bold" fill="white" fontFamily="sans-serif" textAnchor="middle">TRACTION</text>
    <text x="80" y="118" fontSize="5.5" fill="rgba(255,255,255,0.7)" fontFamily="sans-serif" textAnchor="middle">Gabriel Weinberg &amp; Justin Mares</text>
    <rect x="50" y="132" width="60" height="14" rx="3" fill="white" opacity="0.15"/>
    <text x="80" y="142" fontSize="5.5" fill="white" fontFamily="sans-serif" textAnchor="middle">★★★★☆ MUST READ</text>
  </svg>,
];

const FEATURES = [
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
      </svg>
    ),
    title: 'One-click capture',
    desc: 'See a great infographic, book cover, or chart? Save it in one click with our browser extension — before the page disappears forever.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
    ),
    title: 'Your library, your rules',
    desc: 'Group images into folders like "Marketing Ideas" or "Book Covers", and tag them with color-coded categories. No more digging through Downloads.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M21 21l-4.35-4.35M17 11A6 6 0 105 11a6 6 0 0012 0z" />
      </svg>
    ),
    title: 'Find it in seconds',
    desc: 'Search by name, folder, or category across your entire collection. No more "I know I saved that somewhere…"',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
      </svg>
    ),
    title: 'Share with a link',
    desc: 'Send anyone a direct link to any image — colleagues, clients, or your team. No login required on their end.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    title: 'Only yours, always',
    desc: 'Your library is 100% private by default. Images live on your account — not on a public board anyone can stumble on.',
  },
  {
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
          d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Desktop & mobile',
    desc: 'Install Snapvault as a PWA on any device. Browse your collection from your phone the same moment you saved it on your laptop.',
  },
];

const PLANS = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    storage: '500 MB',
    uploads: '50 / month',
    folders: '5 folders',
    highlight: false,
    cta: 'Get started free',
    href: '/register',
  },
  {
    name: 'Pro',
    price: '$9',
    period: '/ month',
    storage: '20 GB',
    uploads: '500 / month',
    folders: 'Unlimited folders',
    highlight: true,
    cta: 'Start Pro',
    href: '/register',
  },
  {
    name: 'Team',
    price: '$29',
    period: '/ month',
    storage: '100 GB',
    uploads: '2,000 / month',
    folders: 'Unlimited folders',
    highlight: false,
    cta: 'Start Team',
    href: '/register',
  },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Install the extension',
    desc: 'Add Snapvault to Chrome, Firefox or Safari in 30 seconds. It sits quietly in your toolbar until you need it.',
  },
  {
    step: '02',
    title: 'Save anything, anywhere',
    desc: 'Right-click an infographic, book cover, or chart — or hit "Capture page" for a full screenshot. It lands in your library instantly.',
  },
  {
    step: '03',
    title: 'Find it when it matters',
    desc: 'Search, filter by folder or category, and pull up exactly what you need — whether you saved it yesterday or a year ago.',
  },
];

export default function LandingPage() {
  const { user } = useAuth();

  return (
    <div className="bg-gray-950 text-gray-100 min-h-screen font-sans">
      {/* ── Nav ───────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-white/5 bg-gray-950/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold text-xl tracking-tight text-white">
            <span className="text-brand-400">⬡</span> Snapvault
          </Link>
          <nav className="hidden sm:flex items-center gap-6 text-sm text-gray-400">
            <a href="#features" className="hover:text-white transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-white transition-colors">How it works</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#download" className="hover:text-white transition-colors">Download</a>
          </nav>
          <div className="flex items-center gap-3">
            {user ? (
              <Link to="/dashboard"
                className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium transition-colors">
                Go to dashboard
              </Link>
            ) : (
              <>
                <Link to="/login"
                  className="hidden sm:inline text-sm text-gray-400 hover:text-white transition-colors">
                  Sign in
                </Link>
                <Link to="/register"
                  className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-sm font-medium transition-colors">
                  Get started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-24 pb-32 px-4 sm:px-6 text-center">
        {/* background glow */}
        <div className="pointer-events-none absolute inset-0 flex items-start justify-center">
          <div className="w-[700px] h-[400px] rounded-full bg-brand-600/20 blur-[120px] -translate-y-1/4" />
        </div>

        <div className="relative max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand-500/30 bg-brand-500/10 text-brand-300 text-xs font-medium mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" />
            Browser extension available for Chrome, Firefox &amp; Edge
          </span>

          <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-white leading-tight mb-6">
            Stop losing the images<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-400 to-indigo-300">
              that inspire you
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-400 max-w-xl mx-auto mb-10">
            Infographics, book covers, charts, diagrams — you find great visual content every day
            and lose it just as fast. Snapvault is your personal vault: save once, find forever.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register"
              className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold text-base transition-all hover:scale-105 shadow-lg shadow-brand-500/30">
              Create free account
            </Link>
            <Link to="/login"
              className="w-full sm:w-auto px-8 py-3.5 rounded-xl border border-white/10 hover:border-white/20 bg-white/5 hover:bg-white/10 text-white font-semibold text-base transition-all">
              Sign in
            </Link>
          </div>

          <p className="mt-4 text-xs text-gray-500">Free forever · No credit card required</p>
        </div>

        {/* mock browser window */}
        <div className="relative max-w-4xl mx-auto mt-20">
          <div className="rounded-2xl border border-white/10 bg-gray-900 shadow-2xl shadow-black/50 overflow-hidden">
            {/* browser chrome */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-gray-900">
              <span className="w-3 h-3 rounded-full bg-red-500/70" />
              <span className="w-3 h-3 rounded-full bg-yellow-500/70" />
              <span className="w-3 h-3 rounded-full bg-green-500/70" />
              <div className="ml-3 flex-1 max-w-xs bg-gray-800 rounded-md px-3 py-1 text-xs text-gray-400">
                app.snapvault.io/dashboard
              </div>
            </div>
            {/* fake dashboard grid */}
            <div className="p-6 bg-gray-950">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 rounded bg-brand-500/60" />
                  <div className="h-4 w-32 rounded bg-gray-700" />
                </div>
                <div className="h-7 w-20 rounded-lg bg-brand-500/50 flex items-center justify-center">
                  <div className="h-2 w-10 rounded bg-white/30" />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {DEMO_CARDS.map((card, i) => (
                  <div key={i}
                    className="aspect-square rounded-xl border border-white/8 overflow-hidden relative group"
                    style={{ opacity: 1 - i * 0.04 }}>
                    {card}
                    {/* hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* glow under card */}
          <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-2/3 h-16 bg-brand-600/20 blur-2xl rounded-full" />
        </div>
      </section>

      {/* ── Pain / Why it matters ─────────────────────────────────── */}
      <section className="py-20 px-4 sm:px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-6 text-center">
            {[
              {
                stat: '78%',
                claim: 'of people re-search content',
                detail: 'they already found once — because they had nowhere reliable to save it.',
              },
              {
                stat: '∞',
                claim: 'browser tabs left open',
                detail: '"I\'ll come back to this." You never do. And when you try, the page is gone.',
              },
              {
                stat: '1 vault',
                claim: 'instead of scattered chaos',
                detail: 'Downloads folder, screenshots, bookmarks, Pinterest boards — all fragmented. Snapvault unifies them.',
              },
            ].map((item) => (
              <div key={item.stat} className="p-8 rounded-2xl bg-gray-900/40 border border-white/8">
                <p className="text-5xl font-black text-brand-400 mb-3">{item.stat}</p>
                <p className="font-semibold text-white mb-2">{item.claim}</p>
                <p className="text-sm text-gray-400 leading-relaxed">{item.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────── */}
      <section id="how-it-works" className="py-24 px-4 sm:px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">How it works</h2>
            <p className="text-gray-400 max-w-md mx-auto">Up and running in under a minute.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-8">
            {HOW_IT_WORKS.map((item) => (
              <div key={item.step} className="relative pl-6 border-l-2 border-brand-500/30 sm:border-l-0 sm:pl-0 sm:text-center">
                <span className="inline-block text-4xl font-black text-brand-500/20 mb-3 sm:text-5xl">{item.step}</span>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ──────────────────────────────────────────────── */}
      <section id="features" className="py-24 px-4 sm:px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Built for visual thinkers</h2>
            <p className="text-gray-400 max-w-lg mx-auto">
              Whether you collect infographics for research, book covers for inspiration, or charts
              for presentations — Snapvault keeps your visual knowledge organised and retrievable.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title}
                className="p-6 rounded-2xl border border-white/8 bg-gray-900/60 hover:border-brand-500/30 hover:bg-gray-900 transition-all group">
                <div className="w-11 h-11 rounded-xl bg-brand-500/15 flex items-center justify-center text-brand-400 mb-4 group-hover:bg-brand-500/25 transition-colors">
                  {f.icon}
                </div>
                <h3 className="font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ───────────────────────────────────────────────── */}
      <section id="pricing" className="py-24 px-4 sm:px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Simple pricing</h2>
            <p className="text-gray-400 max-w-md mx-auto">Start free. Upgrade when you need more.</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {PLANS.map((plan) => (
              <div key={plan.name}
                className={`relative rounded-2xl p-6 flex flex-col border transition-all ${
                  plan.highlight
                    ? 'border-brand-500 bg-brand-500/10 shadow-lg shadow-brand-500/10'
                    : 'border-white/10 bg-gray-900/60'
                }`}>
                {plan.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-brand-500 text-white text-xs font-semibold">
                    Most popular
                  </span>
                )}
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-400 mb-2">{plan.name}</p>
                  <div className="flex items-end gap-1">
                    <span className="text-4xl font-extrabold text-white">{plan.price}</span>
                    <span className="text-gray-400 text-sm pb-1">{plan.period}</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8 flex-1 text-sm">
                  {[plan.storage, plan.uploads, plan.folders].map((feat) => (
                    <li key={feat} className="flex items-center gap-2 text-gray-300">
                      <svg className="w-4 h-4 text-brand-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      {feat}
                    </li>
                  ))}
                </ul>
                <Link to={plan.href}
                  className={`w-full text-center py-2.5 rounded-xl font-semibold text-sm transition-all ${
                    plan.highlight
                      ? 'bg-brand-500 hover:bg-brand-600 text-white shadow-md shadow-brand-500/30'
                      : 'border border-white/10 hover:border-white/20 bg-white/5 hover:bg-white/10 text-white'
                  }`}>
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Download Extension ────────────────────────────────────── */}
      <section id="download" className="py-24 px-4 sm:px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Get the browser extension</h2>
            <p className="text-gray-400 max-w-md mx-auto">
              Install Snapvault in your browser and start saving images with one click — from any website.
            </p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {/* Chrome */}
            <div className="flex flex-col items-center p-8 rounded-2xl border border-white/10 bg-gray-900/60 hover:border-brand-500/30 hover:bg-gray-900 transition-all group">
              <div className="w-16 h-16 mb-5 drop-shadow-lg">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="21" fill="white"/>
                  <circle cx="24" cy="24" r="9.5" fill="#1A73E8"/>
                  <path d="M24 14.5h18.7A21 21 0 0 0 5.7 9.3L14.3 24A9.5 9.5 0 0 1 24 14.5Z" fill="#EA4335"/>
                  <path d="M24 33.5a9.5 9.5 0 0 1-9.7-9.5L5.7 9.3A21 21 0 0 0 5.3 38.7L14 24.2A9.5 9.5 0 0 1 24 33.5Z" fill="#34A853"/>
                  <path d="M24 14.5a9.5 9.5 0 0 1 9.7 9.5 9.5 9.5 0 0 1-9.7 9.5L14 38.8A21 21 0 0 0 42.7 14.5Z" fill="#FBBC05"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">Chrome</h3>
              <p className="text-xs text-gray-500 mb-2">Works on Chrome 88+</p>
              <ol className="text-xs text-gray-500 mb-5 w-full space-y-1 text-left">
                <li><span className="text-brand-400 font-bold">1.</span> Download &amp; unzip the file</li>
                <li><span className="text-brand-400 font-bold">2.</span> Open <span className="font-mono text-gray-400">chrome://extensions</span> → enable <em>Developer mode</em></li>
                <li><span className="text-brand-400 font-bold">3.</span> Click <em>Load unpacked</em> → select the unzipped folder</li>
              </ol>
              <a
                href="/downloads/snapvault-chrome-extension.zip"
                download
                className="w-full text-center py-2.5 px-4 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold text-sm transition-all group-hover:shadow-md group-hover:shadow-brand-500/20"
              >
                ↓ Download for Chrome
              </a>
            </div>

            {/* Firefox */}
            <div className="flex flex-col items-center p-8 rounded-2xl border border-white/10 bg-gray-900/60 hover:border-orange-500/30 hover:bg-gray-900 transition-all group">
              <div className="w-16 h-16 mb-5 drop-shadow-lg">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <radialGradient id="ffA" cx="50%" cy="50%" r="50%">
                      <stop offset="0%" stopColor="#FF980E"/>
                      <stop offset="100%" stopColor="#FF3750"/>
                    </radialGradient>
                    <radialGradient id="ffB" cx="30%" cy="30%" r="70%">
                      <stop offset="0%" stopColor="#FF980E"/>
                      <stop offset="100%" stopColor="#E31587"/>
                    </radialGradient>
                  </defs>
                  <circle cx="24" cy="24" r="21" fill="url(#ffA)"/>
                  <circle cx="24" cy="25" r="12" fill="#0060DF"/>
                  <circle cx="24" cy="25" r="7" fill="#00B3F4"/>
                  <path d="M24 3C12.4 3 3 12.4 3 24c0 3.3.8 6.5 2.2 9.3L24 24 14.7 8.8A20.9 20.9 0 0 1 24 3Z" fill="url(#ffB)" opacity="0.9"/>
                  <path d="M38 9.5C34.1 5.6 28.8 3 24 3c-2.3 0-4.5.5-6.6 1.3L24 16.5c4.1 0 8 2.2 9.8 6.1C35.2 18 37 12 38 9.5Z" fill="#FF980E" opacity="0.8"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">Firefox</h3>
              <p className="text-xs text-gray-500 mb-2">Works on Firefox 109+</p>
              <ol className="text-xs text-gray-500 mb-5 w-full space-y-1 text-left">
                <li><span className="text-orange-400 font-bold">1.</span> Download &amp; unzip the file</li>
                <li><span className="text-orange-400 font-bold">2.</span> Open <span className="font-mono text-gray-400">about:debugging</span> → This Firefox</li>
                <li><span className="text-orange-400 font-bold">3.</span> Click <em>Load Temporary Add-on</em> → select <span className="font-mono text-gray-400">manifest.json</span></li>
              </ol>
              <a
                href="/downloads/snapvault-firefox-extension.zip"
                download
                className="w-full text-center py-2.5 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm transition-all group-hover:shadow-md group-hover:shadow-orange-500/20"
              >
                ↓ Download for Firefox
              </a>
            </div>

            {/* Edge */}
            <div className="flex flex-col items-center p-8 rounded-2xl border border-white/10 bg-gray-900/60 hover:border-blue-500/30 hover:bg-gray-900 transition-all group">
              <div className="w-16 h-16 mb-5 drop-shadow-lg">
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <linearGradient id="edA" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#0078D4"/>
                      <stop offset="100%" stopColor="#00B4F0"/>
                    </linearGradient>
                    <linearGradient id="edB" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1BD9E4"/>
                      <stop offset="100%" stopColor="#0078D4"/>
                    </linearGradient>
                  </defs>
                  <circle cx="24" cy="24" r="21" fill="url(#edA)"/>
                  <path d="M24 8C15.2 8 8 15.2 8 24c0 4.7 2 9 5.2 12C15.4 38.7 19.5 40 24 40c2.4 0 4.7-.5 6.8-1.4C36.9 36.3 40 30.5 40 24 40 15.2 32.8 8 24 8Z" fill="url(#edB)"/>
                  <path d="M14 28c0 5.5 4.5 10 10 10s10-4.5 10-10H14Z" fill="#50D0E0" opacity="0.4"/>
                  <ellipse cx="24" cy="27" rx="12" ry="6" fill="white" opacity="0.15"/>
                  <path d="M12 22c0-6.6 5.4-12 12-12 4 0 7.6 2 9.8 5H24c-3.3 0-6 2.7-6 6H12Z" fill="white" opacity="0.3"/>
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white mb-1">Edge</h3>
              <p className="text-xs text-gray-500 mb-2">Works on Edge 88+</p>
              <ol className="text-xs text-gray-500 mb-5 w-full space-y-1 text-left">
                <li><span className="text-blue-400 font-bold">1.</span> Download &amp; unzip the file</li>
                <li><span className="text-blue-400 font-bold">2.</span> Open <span className="font-mono text-gray-400">edge://extensions</span> → enable <em>Developer mode</em></li>
                <li><span className="text-blue-400 font-bold">3.</span> Click <em>Load unpacked</em> → select the unzipped folder</li>
              </ol>
              <a
                href="/downloads/snapvault-chrome-extension.zip"
                download
                className="w-full text-center py-2.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-all group-hover:shadow-md group-hover:shadow-blue-500/20"
              >
                ↓ Download for Edge
              </a>
            </div>
          </div>

          <p className="text-center text-xs text-gray-600 mt-8">
            Safari support coming soon · All extensions are free · No account needed to install
          </p>
        </div>
      </section>

      {/* ── Mobile / PWA ──────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="rounded-2xl bg-gradient-to-br from-brand-600/20 to-indigo-900/20 border border-brand-500/20 p-8 sm:p-12">
            <div className="grid sm:grid-cols-2 gap-10 items-center">

              {/* Text */}
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-brand-500/30 bg-brand-500/10 text-brand-300 text-xs font-medium mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" />
                  Works on any device
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
                  Add Snapvault to your phone
                </h2>
                <p className="text-gray-400 leading-relaxed mb-6">
                  Install Snapvault as an app on your phone — no App Store needed.
                  Open it like any other app, browse your vault, and upload photos directly from your camera roll.
                </p>
                <ul className="space-y-3 text-sm text-gray-300">
                  {[
                    'Launches full-screen, no browser bar',
                    'Works offline — browse saved images anywhere',
                    'Upload photos straight from your camera',
                    'Free — installs in under 10 seconds',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <span className="w-5 h-5 rounded-full bg-brand-500/20 flex items-center justify-center shrink-0">
                        <svg className="w-3 h-3 text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Install steps */}
              <div className="space-y-4">
                {/* Android */}
                <div className="rounded-xl bg-gray-900/80 border border-white/8 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">🤖</span>
                    <span className="font-semibold text-white text-sm">Android (Chrome / Edge)</span>
                  </div>
                  <ol className="space-y-2 text-xs text-gray-400">
                    <li className="flex gap-2"><span className="text-brand-400 font-bold shrink-0">1.</span>Open Snapvault in Chrome or Edge</li>
                    <li className="flex gap-2"><span className="text-brand-400 font-bold shrink-0">2.</span>Tap the <strong className="text-gray-200">⋮ menu</strong> (top-right)</li>
                    <li className="flex gap-2"><span className="text-brand-400 font-bold shrink-0">3.</span>Tap <strong className="text-gray-200">"Add to Home screen"</strong> and confirm</li>
                  </ol>
                  <p className="mt-3 text-xs text-brand-400 font-medium">
                    💡 A banner may also appear automatically at the bottom of the screen.
                  </p>
                </div>

                {/* iOS */}
                <div className="rounded-xl bg-gray-900/80 border border-white/8 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">🍎</span>
                    <span className="font-semibold text-white text-sm">iPhone / iPad (Safari)</span>
                  </div>
                  <ol className="space-y-2 text-xs text-gray-400">
                    <li className="flex gap-2"><span className="text-brand-400 font-bold shrink-0">1.</span>Open Snapvault in <strong className="text-gray-200">Safari</strong></li>
                    <li className="flex gap-2">
                      <span className="text-brand-400 font-bold shrink-0">2.</span>
                      <span>Tap the <strong className="text-gray-200">Share</strong> button
                        {' '}
                        <svg className="inline w-3.5 h-3.5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                        {' '}at the bottom of the screen
                      </span>
                    </li>
                    <li className="flex gap-2"><span className="text-brand-400 font-bold shrink-0">3.</span>Scroll down and tap <strong className="text-gray-200">"Add to Home Screen"</strong></li>
                    <li className="flex gap-2"><span className="text-brand-400 font-bold shrink-0">4.</span>Tap <strong className="text-gray-200">Add</strong> — done!</li>
                  </ol>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── CTA banner ────────────────────────────────────────────── */}
      <section className="py-24 px-4 sm:px-6 border-t border-white/5">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Your best ideas deserve<br />a better home than a screenshot folder
          </h2>
          <p className="text-gray-400 mb-8 max-w-md mx-auto">
            Every infographic, book cover, and diagram you've ever wished you could find again —
            all in one searchable, beautifully organised vault. Free to start, takes 60 seconds.
          </p>
          <Link to="/register"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold text-base transition-all hover:scale-105 shadow-lg shadow-brand-500/30">
            Create your free account
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────── */}
      <footer className="border-t border-white/5 py-10 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
          <Link to="/" className="flex items-center gap-2 font-semibold text-gray-400 hover:text-white transition-colors">
            <span className="text-brand-400">⬡</span> Snapvault
          </Link>
          <div className="flex items-center gap-6">
            <a href="#features" className="hover:text-gray-300 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-gray-300 transition-colors">Pricing</a>
            <a href="#download" className="hover:text-gray-300 transition-colors">Download</a>
            <Link to="/login" className="hover:text-gray-300 transition-colors">Sign in</Link>
            <Link to="/register" className="hover:text-gray-300 transition-colors">Register</Link>
          </div>
          <p>© {new Date().getFullYear()} Snapvault</p>
        </div>
      </footer>
    </div>
  );
}
